<!-- @component
	Renders a single recommendation card with evaluation results,
	parameter inputs, analysis table, and action buttons.

	Extracted from the `{#each}` loop in `Recommendations.svelte`.

	@param recommendation - The recommendation data object
	@param analyticName - Display name for the analytic
	@param evaluationResult - Combined evaluation result with description
	@param parameterValues - Current parameter values keyed by parameter name
	@param analysisValues - Writable store of analytical values for the table
	@param isOpen - Whether the card should be open by default
	@param editable - Whether the card actions are enabled
	@param parentType - Type of the analytical parent
	@param parentId - ID of the analytical parent
	@param onParameterChanged - Callback when a parameter value changes
	@param onAddAnalysis - Callback to create a separate analysis
	@param onAddValues - Callback to adopt recommendation values
	@param onUpdateEvaluation - Callback when a value is updated in the table
-->
<script lang="ts">
	import { addRenderer } from 'svelecte'
	import { t } from '$i18n/i18n'
	import CollapsibleCard from '../molecules/CollapsibleCard.svelte'
	import SecondaryButton from '../atoms/SecondaryButton.svelte'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import AnalysisTable from './AnalysisTable.svelte'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import { AnalyticalParentType } from '$types/enums'
	import type { Recommendation } from './types/recommendation'
	import type { AnalyticalValue } from './types/analyticalValue'
	import type { Writable } from 'svelte/store'

	interface Props {
		recommendation: Recommendation
		analyticName: string
		evaluationResult?: { result: { description: string } }
		parameterValues: Record<string, string>
		analysisValues: Writable<AnalyticalValue[]>
		isOpen: boolean
		editable: boolean
		parentType: string | null
		parentId: string | null
		onParameterChanged: (analytic: string, parameter: string, data: unknown) => void
		onAddAnalysis: () => void
		onAddValues: () => void
		onUpdateEvaluation: () => void
	}

	let {
		recommendation,
		analyticName,
		evaluationResult,
		parameterValues,
		analysisValues,
		isOpen,
		editable,
		parentType,
		parentId,
		onParameterChanged,
		onAddAnalysis,
		onAddValues,
		onUpdateEvaluation
	}: Props = $props()

	let secondaryLabel = $derived(
		(parentType === AnalyticalParentType.NONE && parentId === '-1') ||
			parentType === AnalyticalParentType.CHECK
			? $t('Analytics.tool.recommendations.createSeparate')
			: $t('Analytics.recommendations.createSeparate')
	)

	let secondaryTitleAttr = $derived(
		(parentType === AnalyticalParentType.NONE && parentId === '-1') ||
			parentType === AnalyticalParentType.CHECK
			? $t('Analytics.tool.recommendations.createSeparate.titleAttr')
			: $t('Analytics.recommendations.createSeparate')
	)

	const addParameterRenderer = (parameter: string) => {
		function renderer(item: object) {
			return $t(`Analytics.evaluationParameter.${parameter}.${(item as { text: string }).text}`)
		}

		addRenderer(parameter + '-render-rec', renderer)
		return renderer
	}
</script>

<CollapsibleCard openByDefault={isOpen} collapseHeader>
	<div slot="header" id={recommendation.analytic} class="recommendation-header">
		<div>
			<h3>{$t('Analytics.recommendations.evaluationAccordingTo')}</h3>
			<h2>{analyticName}</h2>
		</div>
		<div>
			<h4>{$t('Analytics.recommendations.evaluation.overall')}</h4>
			<div class="evaluation">
				{evaluationResult ? evaluationResult.result.description : '-'}
			</div>
		</div>
	</div>
	<div slot="body" class="recommendation">
		<h3>{$t('Analytics.recommendations.evaluationAccordingTo')}</h3>
		<h2 style="padding-bottom: 0.88rem;">{analyticName}</h2>
		<div class="parameter-container">
			{#each Object.entries(recommendation.requiredParameters) as [parameter, parameterOptions] (parameter)}
				{#if parameterValues?.[parameter] !== undefined}
					<AutocompleteInputField
						value={parameterValues[parameter]}
						label={$t(`Analytics.evaluationParameter.${parameter}`)}
						autocompleteConfig={AutocompleteConfig(null, null)}
						options={parameterOptions}
						renderer={addParameterRenderer(parameter)}
						onChange={(data) => onParameterChanged(recommendation.analytic, parameter, data)}
					/>
				{/if}
			{/each}
		</div>

		{#if recommendation.values.length > 0}
			{#key $analysisValues}
				<AnalysisTable {analysisValues} compact on:updateValue={onUpdateEvaluation} />
			{/key}
		{:else}
			<div style="margin-bottom: 25px;">
				{$t('Analytics.recommendations.noFurtherValuesNeeded')}
			</div>
		{/if}

		<h4>{$t('Analytics.recommendations.evaluation.overall')}</h4>
		<div class="evaluation">
			{evaluationResult ? evaluationResult.result.description : '-'}
		</div>

		<div class="buttons">
			<SecondaryButton
				label={secondaryLabel}
				titleAttr={secondaryTitleAttr}
				disabled={!editable}
				on:click={onAddAnalysis}
			/>
			{#if recommendation.values.length > 0}
				<SecondaryButton
					label={$t('Analytics.recommendations.adoptValues')}
					titleAttr={parentType === AnalyticalParentType.NONE || !parentId || parentId === '-1'
						? $t('Analytics.recommendations.adoptValues.titleAttr.notPossible')
						: $t('Analytics.recommendations.adoptValues')}
					disabled={!editable ||
						parentType === AnalyticalParentType.NONE ||
						!parentId ||
						parentId === '-1' ||
						parentType === AnalyticalParentType.CHECK}
					on:click={onAddValues}
				/>
			{/if}
		</div>
	</div>
</CollapsibleCard>

<style lang="scss">
	@import '$styles/global';

	.recommendation h3,
	.recommendation-header h3 {
		@include roboto-font(12px, 500, 10px, #999999);

		text-transform: uppercase;
	}

	.recommendation h2 {
		@include roboto-font(14px, 500, 12px, #000000);
	}

	.recommendation h4,
	.recommendation-header h4 {
		@include roboto-font(18px, 500, 10px, #58c079);

		text-transform: uppercase;
		margin-bottom: 6px;
	}

	.recommendation {
		width: 100%;
		padding: 26px 24px;
		margin-bottom: 10px;
	}

	.recommendation-header {
		width: 100%;
		padding: 26px 24px;
		display: grid;
		grid-template-columns: 60% 40%;
		column-gap: 0.25rem;
	}

	.recommendation-header h2 {
		@include roboto-font(14px, 500, 12px, #000000);

		width: 100%;
		margin-bottom: 0;
	}

	.recommendation :global(.secondary-button) {
		width: unset;
	}

	.buttons {
		@include flex-row(0.75rem, $justify: flex-end);

		margin-top: 1.5rem;
	}

	.evaluation {
		@include roboto-font(12px, 500, 10px, #240b11);
	}

	.parameter-container {
		display: grid;
		grid-template-columns: 50% 50%;
		gap: 10px 5px;
	}
</style>
